import { CHANNEL_NAMES } from '@wms/domain';
import {
  INBOUND_CORRECTION_LABELS,
  INBOUND_STATUSES,
  INBOUND_STATUS_LABELS,
  PACKAGING_LEVEL_LABELS,
  type InboundCorrectionView,
  type InboundSheetResult,
  type InboundShipment,
  type InboundShipmentSummary,
  type InboundStatus,
  type MarketplaceStock,
  type PackagingLevel,
} from '@wms/domain';
import { useMemo, useRef, useState } from 'react';
import { api, apiBlob, errorDetails, errorMessage } from '../../api';
import { downloadBlob, fileToBase64 } from '../../files';
import { fmt } from '../../format';
import { correctionSummary } from '../../receivingFix';
import { navigate } from '../../router';
import { useSession } from '../../session';
import type { CatalogProduct } from '../../types';
import { Empty, ErrorBanner, Modal, Spinner, useLoad, useToast } from '../../ui';
import { Icon } from '../../ui/icons';
import { useSort } from '../../ui/sort';

const STATUS_TONE: Record<InboundStatus, string> = { draft: '', expected: 'info', receiving: 'warn', closed: 'ok', cancelled: 'bad' };
export const InboundStatusChip = ({ status }: { status: InboundStatus }) => <span className={`chip ${STATUS_TONE[status]}`}>{INBOUND_STATUS_LABELS[status]}</span>;

/**
 * Depoya gönderiler (the warehouse calls them mal kabul bildirimi / ASN): what the seller brings to
 * the warehouse. Route #/asn, #/asn/yeni, #/asn/:id, #/asn/:id/duzenle
 */
export function SellerAsn({ id, mode }: { id: string | null; mode: string | null }) {
  if (id === 'yeni') return <AsnEditor shipment={null} />;
  if (id && mode === 'duzenle') return <EditDraft id={id} />;
  if (id) return <AsnDetail id={id} />;
  return <AsnList />;
}

const FILTERS: readonly { label: string; statuses: readonly InboundStatus[] | null }[] = [
  { label: 'Tümü', statuses: null },
  { label: 'Taslak', statuses: ['draft'] },
  { label: 'Yolda', statuses: ['expected'] },
  { label: 'Kabulde', statuses: ['receiving'] },
  { label: 'Tamamlanan', statuses: ['closed'] },
  { label: 'İptal', statuses: ['cancelled'] },
];

/** How a shipment to the warehouse goes, open until the seller has sent its first one. */
function ShipmentGuide({ open }: { open: boolean }) {
  return (
    <details className="card guide" open={open}>
      <summary>
        <Icon name="help" /> Depoya gönderi nasıl yapılır?
      </summary>
      <ol className="card-b">
        <li>
          <b>Gönderi oluşturun.</b> “Yeni gönderi” ile depoya getireceğiniz ürünleri ve adetlerini girin; çok ürün varsa “Excel şablonu”nu indirip
          adetleri yazın ve “Excel'den ekle” ile yükleyin. İlk kurulumda{' '}
          <b>“Pazaryerindeki stoğu ekle”</b> ürünleri Trendyol’daki stok adediyle getirir: göndermediğiniz adetleri düşürün, göndermediğiniz
          ürünü silin. Lot ve son kullanma tarihi takip edilen ürünlerde bunları da yazın.
        </li>
        <li>
          <b>Depoya bildirin.</b> “Kaydet ve depoya bildir” ile depo gönderinizi beklemeye başlar. Önce taslak kaydedip sonra da
          bildirebilirsiniz.
        </li>
        <li>
          <b>Kolileri işaretleyin.</b> Gönderi numarasını (ASN0000001 gibi) kolilerin üzerine ya da irsaliyeye yazın. Nakliye firması ve takip
          numarasını sonradan da ekleyebilirsiniz.
        </li>
        <li>
          <b>Depo kabul eder.</b> Ürünler okutularak sayılır; ilk kez gelen ürünün ölçüsü ve ağırlığı alınır, sonra raflara yerleştirilir.
          Gönderi “Kabulde” görünür. Üzerinde barkod olmayan ürünleri depo sayar ve etiketler; isterseniz Ürünler ve stok ekranından etiket basıp
          yapıştırarak da gönderebilirsiniz.
        </li>
        <li>
          <b>Sonuç.</b> Kabul bitince eksik, fazla ya da hasarlı gelen adetler gönderinin sayfasında yazar. Sağlam ürünler Ürünler ve stok ekranında
          satışa açılır; stok gönderimi açıksa pazaryerlerine de gider.
        </li>
      </ol>
    </details>
  );
}

function AsnList() {
  const list = useLoad(() => api<InboundShipmentSummary[]>('GET', '/api/inbound/shipments'), [], 30_000);
  const [filter, setFilter] = useState(0);
  const statuses = FILTERS[filter]!.statuses;
  const sort = useSort<InboundShipmentSummary>('asn', {
    asn: { label: 'Gönderi no', value: (s) => s.asnNumber },
    reference: { label: 'Referans', value: (s) => s.reference },
    status: { label: 'Durum', value: (s) => INBOUND_STATUSES.indexOf(s.status) },
    expectedAt: { label: 'Tahmini varış', value: (s) => s.expectedAt },
    carrier: { label: 'Taşıyıcı · takip no', value: (s) => s.carrier },
    received: { label: 'Kabul / beklenen', value: (s) => s.receivedUnits, desc: true },
    created: { label: 'Oluşturma', value: (s) => s.createdAt, desc: true },
  });
  const rows = sort.apply((list.data ?? []).filter((s) => !statuses || statuses.includes(s.status)));
  return (
    <>
      {list.data && <ShipmentGuide open={list.data.length === 0} />}
      <div className="row">
        {FILTERS.map((f, i) => (
          <button key={f.label} className={`chip select${filter === i ? ' on' : ''}`} onClick={() => setFilter(i)}>
            {f.label}
          </button>
        ))}
        <span style={{ flex: 1 }} />
        {sort.picker}
        <button className="btn primary" onClick={() => navigate('/asn/yeni')}>
          <Icon name="plus" /> Yeni gönderi
        </button>
      </div>
      <section className="card">
        {list.error && <ErrorBanner message={list.error} onRetry={() => void list.reload()} />}
        {!list.data ? (
          <Spinner />
        ) : rows.length === 0 ? (
          <Empty title="Henüz gönderi yok">Depoya ürün getirmeden önce burada bir gönderi oluşturun: depo neyin geleceğini bilir, gelince hızla sayar.</Empty>
        ) : (
          <div className="table-wrap">
            <table className="t stack-sm">
              <thead>
                <tr>
                  {sort.th('asn')}
                  {sort.th('reference', 'hide-sm')}
                  {sort.th('status')}
                  {sort.th('expectedAt', 'hide-sm')}
                  {sort.th('carrier', 'hide-sm')}
                  {sort.th('received', 'num')}
                  {sort.th('created', 'hide-sm')}
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => (
                  <tr key={s.id} className="clickable" onClick={() => navigate(`/asn/${s.id}`)}>
                    <td className="mono">{s.asnNumber}</td>
                    <td className="hide-sm">{s.reference ?? '—'}</td>
                    <td>
                      <InboundStatusChip status={s.status} />
                    </td>
                    <td className="hide-sm">{s.expectedAt ? fmt.date(s.expectedAt) : '—'}</td>
                    <td className="hide-sm">
                      {s.carrier ?? '—'}
                      {s.trackingNumber && <span className="mono muted"> · {s.trackingNumber}</span>}
                    </td>
                    <td className="num" data-label="Kabul">
                      {fmt.n(s.receivedUnits)} / {fmt.n(s.expectedUnits)}
                    </td>
                    <td className="nowrap hide-sm">{fmt.dateTime(s.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

function EditDraft({ id }: { id: string }) {
  const shipment = useLoad(() => api<InboundShipment>('GET', `/api/inbound/shipments/${id}`), [id]);
  if (shipment.error) return <ErrorBanner message={shipment.error} onRetry={() => void shipment.reload()} />;
  if (!shipment.data) return <Spinner />;
  if (shipment.data.status !== 'draft') return <ErrorBanner message="Bu gönderi depoya bildirilmiş; satırları değiştirilemez." />;
  return <AsnEditor shipment={shipment.data} />;
}

interface LineDraft {
  readonly key: number;
  readonly productId: string;
  readonly packaging: PackagingLevel;
  readonly qty: number;
  readonly lotNo: string;
  readonly expiryDate: string;
  /** where the count came from ("Trendyol: 12") */
  readonly hint?: string;
}

let nextKey = 1;

/** New ASN or a draft being edited: header, then one line per product and pack size. */
function AsnEditor({ shipment }: { shipment: InboundShipment | null }) {
  const { warehouses } = useSession();
  const toast = useToast();
  const catalog = useLoad(() => api<CatalogProduct[]>('GET', '/api/catalog/products'), []);
  const [warehouseId, setWarehouseId] = useState(shipment?.warehouseId ?? warehouses[0]?.id ?? '');
  const [reference, setReference] = useState(shipment?.reference ?? '');
  const [expectedAt, setExpectedAt] = useState(shipment?.expectedAt ?? '');
  const [carrier, setCarrier] = useState(shipment?.carrier ?? '');
  const [trackingNumber, setTrackingNumber] = useState(shipment?.trackingNumber ?? '');
  const [notes, setNotes] = useState(shipment?.notes ?? '');
  const [lines, setLines] = useState<LineDraft[]>(
    () =>
      shipment?.lines.map((l) => ({
        key: nextKey++,
        productId: l.productId,
        packaging: l.packagingLevel,
        qty: l.expectedQty / l.unitsPerPack,
        lotNo: l.lotNo ?? '',
        expiryDate: l.expiryDate ?? '',
      })) ?? [],
  );
  const [problem, setProblem] = useState<{ message: string; details: string[] } | null>(null);
  const [busy, setBusy] = useState(false);
  const [reading, setReading] = useState(false);
  const sheetInput = useRef<HTMLInputElement>(null);

  const products = useMemo(() => new Map((catalog.data ?? []).map((p) => [p.id, p])), [catalog.data]);
  const unitsPerPack = (line: LineDraft) => (line.packaging === 'each' ? 1 : (products.get(line.productId)?.packagings.find((k) => k.level === line.packaging)?.unitsPerPack ?? 1));
  const totalUnits = lines.reduce((n, l) => n + l.qty * unitsPerPack(l), 0);

  const update = (key: number, patch: Partial<LineDraft>) => setLines((all) => all.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const addLine = () => {
    const first = catalog.data?.[0];
    if (first) setLines((all) => [...all, { key: nextKey++, productId: first.id, packaging: 'each', qty: 1, lotNo: '', expiryDate: '' }]);
  };

  // the opening stock: every product with the count its store shows, to be lowered where less is sent
  const fromStores = async () => {
    setReading(true);
    try {
      const stock = await api<MarketplaceStock>('POST', '/api/mapping/marketplace-stock');
      const present = new Set(lines.map((l) => l.productId));
      const added = stock.lines.filter((l) => !present.has(l.productId));
      setLines((all) => [
        ...all,
        ...added.map((l) => ({
          key: nextKey++,
          productId: l.productId,
          packaging: 'each' as const,
          qty: l.qty,
          lotNo: '',
          expiryDate: '',
          hint: l.sources.map((s) => `${CHANNEL_NAMES[s.channel] ?? s.channel}: ${fmt.n(s.qty)}`).join(' · '),
        })),
      ]);
      const units = added.reduce((n, l) => n + l.qty, 0);
      for (const p of stock.problems) toast(`${p.account}: ${p.message}`, 'bad');
      if (added.length > 0) toast(`${added.length} ürün, ${fmt.n(units)} adet eklendi. Daha az göndereceklerinizin adedini düşürün.`, 'ok');
      else if (stock.problems.length === 0) toast(stock.lines.length > 0 ? 'Bu ürünler zaten listede' : 'Pazaryerinde stoğu olan eşleşmiş ürün yok', 'info');
    } catch (e) {
      toast(errorMessage(e), 'bad');
    } finally {
      setReading(false);
    }
  };

  // the Excel template: the seller's products listed, the count to type
  const template = async () => {
    try {
      downloadBlob(await apiBlob('/api/inbound/sheet-template'), 'depoya-gonderi-sablonu.xlsx');
    } catch (e) {
      toast(errorMessage(e), 'bad');
    }
  };

  // an Excel / CSV sheet: its products join the list (a product and lot already listed takes the sheet's count)
  const fromSheet = async (file: File) => {
    setReading(true);
    setProblem(null);
    try {
      const read = await api<InboundSheetResult>('POST', '/api/inbound/sheet', { contentBase64: await fileToBase64(file) });
      setLines((all) => {
        const next = [...all];
        for (const l of read.lines) {
          const same = next.findIndex((x) => x.productId === l.productId && x.packaging === 'each' && x.lotNo === (l.lotNo ?? '') && x.expiryDate === (l.expiryDate ?? ''));
          if (same >= 0) next[same] = { ...next[same]!, qty: l.qty };
          else next.push({ key: nextKey++, productId: l.productId, packaging: 'each', qty: l.qty, lotNo: l.lotNo ?? '', expiryDate: l.expiryDate ?? '' });
        }
        return next;
      });
      const units = read.lines.reduce((n, l) => n + l.qty, 0);
      if (read.lines.length > 0) toast(`Excel'den ${read.lines.length} satır, ${fmt.n(units)} adet eklendi`, 'ok');
      else if (read.errors.length === 0) toast('Dosyada adedi yazılmış ürün yok', 'info');
      if (read.errors.length > 0) {
        const said = read.errors.slice(0, 20).map((e) => `${e.row}. satır${e.column ? ` · ${e.column}` : ''}: ${e.message}`);
        if (read.errors.length > 20) said.push(`ve ${read.errors.length - 20} satır daha`);
        setProblem({ message: `${read.errors.length} satır eklenmedi; dosyada düzeltip yeniden yükleyin.`, details: said });
      }
    } catch (e) {
      toast(errorMessage(e), 'bad');
    } finally {
      setReading(false);
      if (sheetInput.current) sheetInput.current.value = '';
    }
  };

  const save = async (send: boolean) => {
    setBusy(true);
    setProblem(null);
    const body = {
      warehouseId,
      reference: reference.trim() || null,
      expectedAt: expectedAt || null,
      carrier: carrier.trim() || null,
      trackingNumber: trackingNumber.trim() || null,
      notes: notes.trim() || null,
      lines: lines.map((l) => ({
        productId: l.productId,
        ...(l.packaging === 'each' ? { expectedQty: l.qty } : { packaging: l.packaging, packs: l.qty }),
        lotNo: l.lotNo.trim() || null,
        expiryDate: l.expiryDate || null,
      })),
    };
    try {
      let saved: InboundShipment;
      if (shipment) {
        saved = await api<InboundShipment>('PUT', `/api/inbound/shipments/${shipment.id}`, body);
        if (send) saved = await api<InboundShipment>('POST', `/api/inbound/shipments/${shipment.id}/submit`);
      } else {
        saved = await api<InboundShipment>('POST', '/api/inbound/shipments', { ...body, draft: !send });
      }
      toast(send ? `${saved.asnNumber} depoya bildirildi` : `${saved.asnNumber} taslak olarak kaydedildi`, 'ok');
      navigate(`/asn/${saved.id}`);
    } catch (e) {
      setProblem({ message: errorMessage(e), details: errorDetails(e) });
      setBusy(false);
    }
  };

  if (catalog.error) return <ErrorBanner message={catalog.error} onRetry={() => void catalog.reload()} />;
  if (!catalog.data) return <Spinner />;

  return (
    <>
      <div className="row">
        <a className="btn ghost" href={shipment ? `#/asn/${shipment.id}` : '#/asn'}>
          <Icon name="back" /> Geri
        </a>
        <h2>{shipment ? `${shipment.asnNumber} taslağını düzenle` : 'Yeni gönderi: depoya ürün getiriyorum'}</h2>
      </div>
      {problem && (
        <div className="banner bad">
          <div>
            {problem.message}
            {problem.details.length > 0 && (
              <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontWeight: 500 }}>
                {problem.details.map((d) => (
                  <li key={d}>{d}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
      <section className="card">
        <div className="card-h">
          <h2>Gönderi bilgisi</h2>
          <span className="sub">Ürünler yola çıkınca takip numarasını sonradan da girebilirsiniz</span>
        </div>
        <div className="card-b form-grid">
          {warehouses.length > 1 && (
            <div className="field">
              <label htmlFor="asn-wh">Depo</label>
              <select id="asn-wh" className="select" value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
                {warehouses.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.code} · {w.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="field">
            <label htmlFor="asn-ref">Referans (sipariş / irsaliye no)</label>
            <input id="asn-ref" className="input" value={reference} onChange={(e) => setReference(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="asn-date">Tahmini varış tarihi</label>
            <input id="asn-date" className="input" type="date" value={expectedAt} onChange={(e) => setExpectedAt(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="asn-carrier">Nakliye firması</label>
            <input id="asn-carrier" className="input" value={carrier} onChange={(e) => setCarrier(e.target.value)} placeholder="Aras Kargo, kendi aracımız…" />
          </div>
          <div className="field">
            <label htmlFor="asn-tracking">Nakliye takip numarası</label>
            <input id="asn-tracking" className="input mono" value={trackingNumber} onChange={(e) => setTrackingNumber(e.target.value)} />
          </div>
          <div className="field wide">
            <label htmlFor="asn-notes">Not</label>
            <input id="asn-notes" className="input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Ör. paletler streçli, kırılacak ürün var" />
          </div>
        </div>
      </section>

      <section className="card">
        <div className="card-h">
          <h2>Ürünler</h2>
          <span className="sub">
            {lines.length} satır · {fmt.n(totalUnits)} adet
          </span>
          <span className="spacer" />
          <button className="btn ghost" onClick={() => void template()} title="Ürünlerinizin listelendiği Excel; göndereceklerinizin adedini yazın">
            <Icon name="download" /> Excel şablonu
          </button>
          <button className="btn" onClick={() => sheetInput.current?.click()} disabled={reading} title="Doldurduğunuz Excel ya da CSV dosyasındaki ürünleri ekler">
            <Icon name="upload" /> {reading ? 'Okunuyor…' : "Excel'den ekle"}
          </button>
          <input
            ref={sheetInput}
            type="file"
            hidden
            accept=".xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void fromSheet(file);
            }}
          />
          <button className="btn" onClick={() => void fromStores()} disabled={reading} title="Eşleşmiş ürünleri pazaryerindeki stok adetleriyle ekler">
            <Icon name="download" /> {reading ? 'Okunuyor…' : 'Pazaryerindeki stoğu ekle'}
          </button>
          <button className="btn" onClick={addLine} disabled={catalog.data.length === 0}>
            <Icon name="plus" /> Satır ekle
          </button>
        </div>
        <div className="card-b">
          {lines.length === 0 && (
            <Empty title="Henüz ürün eklenmedi">
              “Satır ekle” ile göndereceğiniz ürünleri seçin, “Excel şablonu”nu indirip adetleri yazıp “Excel'den ekle” ile yükleyin ya da “Pazaryerindeki stoğu
              ekle” ile hepsini Trendyol’daki adetleriyle getirin.
            </Empty>
          )}
          {lines.map((line) => {
            const product = products.get(line.productId);
            return (
              <div key={line.key} className="line-editor">
                <div className="field">
                  <label>Ürün</label>
                  <select className="select" value={line.productId} onChange={(e) => update(line.key, { productId: e.target.value, packaging: 'each' })}>
                    {catalog.data!.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.sku} — {p.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="field">
                  <label>Paketleme tipi</label>
                  <select className="select" value={line.packaging} onChange={(e) => update(line.key, { packaging: e.target.value as PackagingLevel })}>
                    <option value="each">Adet</option>
                    {product?.packagings.map((k) => (
                      <option key={k.level} value={k.level}>
                        {PACKAGING_LEVEL_LABELS[k.level]} ({k.unitsPerPack} adet)
                      </option>
                    ))}
                  </select>
                </div>
                <div className="field">
                  <label>
                    {line.packaging === 'each' ? 'Adet' : `${PACKAGING_LEVEL_LABELS[line.packaging]} sayısı`}
                    {line.hint && <span className="muted"> · {line.hint}</span>}
                  </label>
                  <input className="input" type="number" min={1} value={line.qty} onChange={(e) => update(line.key, { qty: Math.max(1, Math.floor(Number(e.target.value) || 1)) })} />
                </div>
                <div className="field">
                  <label>Lot</label>
                  <input
                    className="input mono"
                    value={line.lotNo}
                    disabled={!product?.trackLot}
                    placeholder={product?.trackLot ? 'Zorunlu' : '—'}
                    onChange={(e) => update(line.key, { lotNo: e.target.value.toUpperCase() })}
                  />
                </div>
                <div className="field">
                  <label>Son kullanma</label>
                  <input className="input" type="date" value={line.expiryDate} disabled={!product?.trackExpiry} onChange={(e) => update(line.key, { expiryDate: e.target.value })} />
                </div>
                <div className="row" style={{ paddingBottom: 4 }}>
                  <span className="units nowrap">= {fmt.n(line.qty * unitsPerPack(line))} adet</span>
                  <button className="icon-btn" onClick={() => setLines((all) => all.filter((l) => l.key !== line.key))} aria-label="Satırı sil">
                    <Icon name="trash" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <div className="row">
        <button className="btn" disabled={busy || lines.length === 0} onClick={() => void save(false)}>
          Taslak olarak kaydet
        </button>
        <button className="btn primary" disabled={busy || lines.length === 0} onClick={() => void save(true)}>
          <Icon name="send" /> Kaydet ve depoya bildir
        </button>
      </div>
    </>
  );
}

const STEPS: readonly { status: InboundStatus; label: string }[] = [
  { status: 'draft', label: 'Taslak' },
  { status: 'expected', label: 'Yolda' },
  { status: 'receiving', label: 'Kısmi kabul edildi' },
  { status: 'closed', label: 'Tamamlandı' },
];

function AsnDetail({ id }: { id: string }) {
  const toast = useToast();
  const loaded = useLoad(() => api<InboundShipment>('GET', `/api/inbound/shipments/${id}`), [id], 30_000);
  // what the warehouse corrected in the count, and why
  const corrections = useLoad(() => api<InboundCorrectionView[]>('GET', `/api/inbound/shipments/${id}/corrections`), [id], 60_000);
  const [shipment, setShipment] = useState<InboundShipment | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [transport, setTransport] = useState<{ expectedAt: string; carrier: string; trackingNumber: string } | null>(null);
  const current = shipment ?? loaded.data ?? null;

  if (loaded.error && !current) return <ErrorBanner message={loaded.error} onRetry={() => void loaded.reload()} />;
  if (!current) return <Spinner />;

  const act = async (work: () => Promise<InboundShipment>, done: string) => {
    try {
      setShipment(await work());
      toast(done, 'ok');
    } catch (e) {
      toast(errorMessage(e), 'bad');
    }
  };
  const stepIndex = STEPS.findIndex((s) => s.status === current.status);
  const expected = current.lines.reduce((n, l) => n + l.expectedQty, 0);
  const received = current.lines.reduce((n, l) => n + l.receivedQty, 0);
  const damaged = current.lines.reduce((n, l) => n + l.damagedQty, 0);

  return (
    <>
      <div className="row">
        <a className="btn ghost" href="#/asn">
          <Icon name="back" /> Gönderiler
        </a>
        <h2 className="mono">{current.asnNumber}</h2>
        <InboundStatusChip status={current.status} />
        <span style={{ flex: 1 }} />
        {current.status === 'draft' && (
          <>
            <button className="btn" onClick={() => navigate(`/asn/${current.id}/duzenle`)}>
              <Icon name="edit" /> Düzenle
            </button>
            <button className="btn primary" onClick={() => void act(() => api('POST', `/api/inbound/shipments/${current.id}/submit`), `${current.asnNumber} depoya bildirildi`)}>
              <Icon name="send" /> Depoya bildir
            </button>
          </>
        )}
        {(current.status === 'draft' || current.status === 'expected') && (
          <button className="btn" onClick={() => setCancelling(true)}>
            <Icon name="trash" /> {current.status === 'draft' ? 'Taslağı sil' : 'İptal et'}
          </button>
        )}
      </div>

      {current.status !== 'cancelled' && (
        <div className="timeline">
          {STEPS.map((s, i) => (
            <span key={s.status} className={i < stepIndex ? 'done' : i === stepIndex ? 'now' : ''}>
              {s.label}
            </span>
          ))}
        </div>
      )}
      {current.status === 'expected' && (
        <div className="banner info">
          Depo bu gönderiyi bekliyor. Kolilerin üzerine ya da irsaliyeye <b className="mono">{current.asnNumber}</b> yazın; depo bu numarayla kabule başlar.
        </div>
      )}
      {current.status === 'closed' && (
        <div className={`banner ${received + damaged === expected ? 'ok' : 'warn'}`}>
          Kabul tamamlandı: {fmt.n(received)} adet sağlam{damaged > 0 && `, ${fmt.n(damaged)} adet hasarlı`} · beklenen {fmt.n(expected)}
          {received + damaged !== expected && ` (fark ${fmt.n(received + damaged - expected)})`}
        </div>
      )}

      <div className="split">
        <section className="card">
          <div className="card-h">
            <h2>Ürünler</h2>
            <span className="sub">
              {fmt.n(received)} / {fmt.n(expected)} adet kabul edildi
            </span>
          </div>
          <div className="table-wrap">
            <table className="t">
              <thead>
                <tr>
                  <th>Ürün</th>
                  <th>Paketleme</th>
                  <th className="num">Beklenen</th>
                  <th className="num">Kabul</th>
                  <th className="num">Hasarlı</th>
                </tr>
              </thead>
              <tbody>
                {current.lines.map((l) => (
                  <tr key={l.id}>
                    <td>
                      <b>{l.productName}</b>
                      <div className="mono muted" style={{ fontSize: 12 }}>
                        {l.sku}
                        {l.lotNo && ` · Lot ${l.lotNo}`}
                        {l.expiryDate && ` · SKT ${fmt.date(l.expiryDate)}`}
                      </div>
                      {l.shortMarkedAt && l.receivedQty + l.damagedQty < l.expectedQty && (
                        <span className="chip bad" title="Depo kalanın gelmediğini işaretledi">
                          Eksik geldi: {fmt.n(l.receivedQty + l.damagedQty)}/{fmt.n(l.expectedQty)}
                        </span>
                      )}
                    </td>
                    <td>{l.packagingLevel === 'each' ? 'Adet' : `${fmt.n(l.expectedQty / l.unitsPerPack)} ${PACKAGING_LEVEL_LABELS[l.packagingLevel].toLocaleLowerCase('tr-TR')} × ${l.unitsPerPack}`}</td>
                    <td className="num">{fmt.n(l.expectedQty)}</td>
                    <td className="num">{fmt.n(l.receivedQty)}</td>
                    <td className="num">{l.damagedQty ? <span className="chip bad">{l.damagedQty}</span> : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="card">
          <div className="card-h">
            <h2>Nakliye</h2>
            <span className="spacer" />
            {(current.status === 'draft' || current.status === 'expected') && !transport && (
              <button
                className="btn sm"
                onClick={() => setTransport({ expectedAt: current.expectedAt ?? '', carrier: current.carrier ?? '', trackingNumber: current.trackingNumber ?? '' })}
              >
                <Icon name="edit" size={16} /> Güncelle
              </button>
            )}
          </div>
          <div className="card-b stack">
            {transport ? (
              <>
                <div className="field">
                  <label htmlFor="tr-date">Tahmini varış</label>
                  <input id="tr-date" className="input" type="date" value={transport.expectedAt} onChange={(e) => setTransport({ ...transport, expectedAt: e.target.value })} />
                </div>
                <div className="field">
                  <label htmlFor="tr-carrier">Nakliye firması</label>
                  <input id="tr-carrier" className="input" value={transport.carrier} onChange={(e) => setTransport({ ...transport, carrier: e.target.value })} />
                </div>
                <div className="field">
                  <label htmlFor="tr-no">Takip numarası</label>
                  <input id="tr-no" className="input mono" value={transport.trackingNumber} onChange={(e) => setTransport({ ...transport, trackingNumber: e.target.value })} />
                </div>
                <div className="row">
                  <button className="btn" onClick={() => setTransport(null)}>
                    Vazgeç
                  </button>
                  <button
                    className="btn primary"
                    onClick={() =>
                      void act(
                        () =>
                          api('PATCH', `/api/inbound/shipments/${current.id}`, {
                            expectedAt: transport.expectedAt || null,
                            carrier: transport.carrier.trim() || null,
                            trackingNumber: transport.trackingNumber.trim() || null,
                          }),
                        'Nakliye bilgisi güncellendi',
                      ).then(() => setTransport(null))
                    }
                  >
                    Kaydet
                  </button>
                </div>
              </>
            ) : (
              <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 16px' }}>
                <dt className="muted">Referans</dt>
                <dd style={{ margin: 0 }}>{current.reference ?? '—'}</dd>
                <dt className="muted">Tahmini varış</dt>
                <dd style={{ margin: 0 }}>{current.expectedAt ? fmt.date(current.expectedAt) : '—'}</dd>
                <dt className="muted">Nakliye</dt>
                <dd style={{ margin: 0 }}>{current.carrier ?? '—'}</dd>
                <dt className="muted">Takip no</dt>
                <dd style={{ margin: 0 }} className="mono">
                  {current.trackingNumber ?? '—'}
                </dd>
                <dt className="muted">Depoya ulaştı</dt>
                <dd style={{ margin: 0 }}>{current.arrivedAt ? fmt.dateTime(current.arrivedAt) : '—'}</dd>
                {current.notes && (
                  <>
                    <dt className="muted">Not</dt>
                    <dd style={{ margin: 0 }}>{current.notes}</dd>
                  </>
                )}
              </dl>
            )}
          </div>
        </section>
      </div>

      {(corrections.data ?? []).length > 0 && (
        <section className="card">
          <div className="card-h">
            <h2>Depo düzeltmeleri</h2>
            <span className="sub">Kabulde sayılan adetler depo tarafından düzeltildi</span>
          </div>
          <div className="table-wrap">
            <table className="t stack-sm">
              <tbody>
                {[...(corrections.data ?? [])].reverse().map((c) => (
                  <tr key={c.id}>
                    <td className="muted" data-label="Zaman">
                      {fmt.dateTime(c.correctedAt)}
                    </td>
                    <td>
                      <b>{c.productName}</b> <span className="mono muted">{c.sku}</span>
                      <div style={{ fontSize: 13 }}>
                        {INBOUND_CORRECTION_LABELS[c.kind]}: {correctionSummary(c)}
                      </div>
                      {c.note && <div className="muted" style={{ fontSize: 13 }}>“{c.note}”</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {cancelling && (
        <Modal
          title={current.status === 'draft' ? 'Taslak silinsin mi?' : `${current.asnNumber} iptal edilsin mi?`}
          onClose={() => setCancelling(false)}
          footer={
            <>
              <button className="btn" onClick={() => setCancelling(false)}>
                Vazgeç
              </button>
              <button
                className="btn danger"
                onClick={() => void act(() => api('POST', `/api/inbound/shipments/${current.id}/cancel`), 'Bildirim iptal edildi').then(() => setCancelling(false))}
              >
                {current.status === 'draft' ? 'Taslağı sil' : 'Gönderiyi iptal et'}
              </button>
            </>
          }
        >
          <p>{current.status === 'draft' ? 'Taslak iptal edilir ve listede “İptal” olarak kalır.' : 'Depo bu gönderiyi artık beklemez. Ürünler yola çıktıysa depoyla görüşün.'}</p>
        </Modal>
      )}
    </>
  );
}
